import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Plus, ClipboardCheck, Calendar, Edit, History, ChevronDown, ChevronRight } from 'lucide-react';
import { DatePickerField } from '../components/DatePickerField';
import { format } from 'date-fns';

// Standard byproduct categories
const BYPRODUCTS = ['Bran', 'Germ', 'Unga Baridi', 'Others'];

export default function DailyClose() {
  const [dailyCloses, setDailyCloses] = useState([]);
  const [packSizes, setPackSizes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [editHistory, setEditHistory] = useState([]);
  
  // Filter state
  const [filterFactory, setFilterFactory] = useState('all');
  
  // Form state
  const [editingId, setEditingId] = useState(null);
  const [formFactory, setFormFactory] = useState('');
  const [formDate, setFormDate] = useState(new Date());
  const [formTime, setFormTime] = useState('');
  const [formNotes, setFormNotes] = useState('');
  
  // Stock entries state - grouped by product type
  const [rawMaizeStock, setRawMaizeStock] = useState({ opening: '', closing: '' });
  const [finishedGoodsStock, setFinishedGoodsStock] = useState([]);
  const [byproductStock, setByproductStock] = useState([]);
  
  // Expanded row state
  const [expandedRows, setExpandedRows] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [closesRes, packSizesRes] = await Promise.all([
        api.get('/daily-close'),
        api.get('/pack-sizes')
      ]);
      setDailyCloses(closesRes.data);
      setPackSizes(packSizesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const initializeForm = (factory = '') => {
    setFormFactory(factory);
    setFormDate(new Date());
    setFormTime(format(new Date(), 'HH:mm'));
    setFormNotes('');
    setRawMaizeStock({ opening: '', closing: '' });
    
    // Initialize finished goods with pack sizes
    setFinishedGoodsStock(packSizes.map(ps => ({
      pack_size_id: ps.id,
      pack_size_kg: ps.size_kg,
      opening: '',
      closing: ''
    })));
    
    // Initialize byproducts
    setByproductStock(BYPRODUCTS.map(name => ({
      name,
      opening: '',
      closing: ''
    })));
  };

  const openCreateDialog = () => {
    setEditingId(null);
    initializeForm();
    setDialogOpen(true);
  };

  const openEditDialog = (record) => {
    setEditingId(record.id);
    setFormFactory(record.factory);
    setFormDate(new Date(record.date));
    setFormTime(record.time || format(new Date(), 'HH:mm'));
    setFormNotes(record.notes || '');
    
    // Parse stock entries from record
    const rawEntry = record.stock_entries?.find(e => e.product_type === 'raw_maize');
    setRawMaizeStock({
      opening: rawEntry?.opening_stock?.toString() || '',
      closing: rawEntry?.closing_stock?.toString() || ''
    });
    
    // Finished goods
    setFinishedGoodsStock(packSizes.map(ps => {
      const entry = record.stock_entries?.find(e => e.product_type === 'finished_goods' && e.pack_size_id === ps.id);
      return {
        pack_size_id: ps.id,
        pack_size_kg: ps.size_kg,
        opening: entry?.opening_stock?.toString() || '',
        closing: entry?.closing_stock?.toString() || ''
      };
    }));
    
    // Byproducts
    setByproductStock(BYPRODUCTS.map(name => {
      const entry = record.stock_entries?.find(e => e.product_type === 'byproduct' && e.byproduct_name === name);
      return {
        name,
        opening: entry?.opening_stock?.toString() || '',
        closing: entry?.closing_stock?.toString() || ''
      };
    }));
    
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formFactory) {
      toast.error('Please select a factory');
      return;
    }
    
    // Build stock entries array
    const stockEntries = [];
    
    // Raw maize
    if (rawMaizeStock.opening || rawMaizeStock.closing) {
      stockEntries.push({
        product_type: 'raw_maize',
        opening_stock: parseFloat(rawMaizeStock.opening) || 0,
        closing_stock: parseFloat(rawMaizeStock.closing) || 0
      });
    }
    
    // Finished goods
    finishedGoodsStock.forEach(fg => {
      if (fg.opening || fg.closing) {
        stockEntries.push({
          product_type: 'finished_goods',
          pack_size_id: fg.pack_size_id,
          pack_size_kg: fg.pack_size_kg,
          opening_stock: parseFloat(fg.opening) || 0,
          closing_stock: parseFloat(fg.closing) || 0
        });
      }
    });
    
    // Byproducts
    byproductStock.forEach(bp => {
      if (bp.opening || bp.closing) {
        stockEntries.push({
          product_type: 'byproduct',
          byproduct_name: bp.name,
          opening_stock: parseFloat(bp.opening) || 0,
          closing_stock: parseFloat(bp.closing) || 0
        });
      }
    });
    
    const payload = {
      factory: formFactory,
      date: format(formDate, 'yyyy-MM-dd'),
      time: formTime,
      stock_entries: stockEntries,
      notes: formNotes || null
    };
    
    try {
      if (editingId) {
        await api.put(`/daily-close/${editingId}`, payload);
        toast.success('Daily close updated');
      } else {
        await api.post('/daily-close', payload);
        toast.success('Daily close recorded');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save daily close');
    }
  };

  const viewEditHistory = async (record) => {
    try {
      const res = await api.get(`/daily-close/${record.id}/edit-history`);
      setEditHistory(res.data.edit_history || []);
      setSelectedRecord(record);
      setHistoryDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load edit history');
    }
  };

  const toggleRowExpand = (id) => {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-KE').format(num || 0);
  };

  // Filter daily closes
  const filteredCloses = dailyCloses.filter(dc => {
    if (filterFactory !== 'all' && dc.factory !== filterFactory) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1B4D3E]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="daily-close-page">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-[#1B4D3E]">Daily Close</h1>
          <p className="text-slate-500 mt-1">Record daily opening and closing stock by factory</p>
        </div>
        <Button className="btn-primary gap-2 self-start" onClick={openCreateDialog} data-testid="add-daily-close-btn">
          <Plus className="h-4 w-4" />
          New Daily Close
        </Button>
      </div>

      {/* Filter */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Label>Factory:</Label>
              <Select value={filterFactory} onValueChange={setFilterFactory}>
                <SelectTrigger className="w-40" data-testid="filter-factory">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Factories</SelectItem>
                  <SelectItem value="Ndumberi">Ndumberi</SelectItem>
                  <SelectItem value="Githunguri">Githunguri</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="text-sm text-slate-500">
              Showing {filteredCloses.length} records
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Daily Close Records */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5" />
            Daily Close Records
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Time</TableHead>
                <TableHead>Factory</TableHead>
                <TableHead>Items</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead className="text-center">Edits</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCloses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                    No daily close records found
                  </TableCell>
                </TableRow>
              ) : (
                filteredCloses.map((dc) => (
                  <React.Fragment key={dc.id}>
                    <TableRow className="cursor-pointer hover:bg-slate-50" data-testid={`daily-close-row-${dc.id}`}>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() => toggleRowExpand(dc.id)}
                        >
                          {expandedRows[dc.id] ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </Button>
                      </TableCell>
                      <TableCell className="font-medium">{dc.date}</TableCell>
                      <TableCell>{dc.time || '-'}</TableCell>
                      <TableCell>
                        <Badge variant={dc.factory === 'Ndumberi' ? 'default' : 'secondary'}>
                          {dc.factory}
                        </Badge>
                      </TableCell>
                      <TableCell>{dc.stock_entries?.length || 0} items</TableCell>
                      <TableCell className="max-w-[200px] truncate">{dc.notes || '-'}</TableCell>
                      <TableCell className="text-center">
                        {dc.edit_history?.length > 0 ? (
                          <Badge variant="outline" className="bg-amber-50 text-amber-700">
                            {dc.edit_history.length} edits
                          </Badge>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(dc)}
                            title="Edit"
                            data-testid={`edit-daily-close-${dc.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          {dc.edit_history?.length > 0 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => viewEditHistory(dc)}
                              title="View History"
                              data-testid={`history-daily-close-${dc.id}`}
                            >
                              <History className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                    
                    {/* Expanded row showing stock details */}
                    {expandedRows[dc.id] && (
                      <TableRow className="bg-slate-50">
                        <TableCell colSpan={8} className="p-4">
                          <div className="grid grid-cols-3 gap-4">
                            {/* Raw Maize */}
                            <div>
                              <p className="font-semibold text-amber-700 mb-2">Raw Maize (kg)</p>
                              {dc.stock_entries?.filter(e => e.product_type === 'raw_maize').map((entry, idx) => (
                                <div key={idx} className="text-sm">
                                  <span className="text-slate-500">Opening:</span> {formatNumber(entry.opening_stock)} |
                                  <span className="text-slate-500 ml-2">Closing:</span> {formatNumber(entry.closing_stock)}
                                </div>
                              ))}
                              {!dc.stock_entries?.some(e => e.product_type === 'raw_maize') && (
                                <span className="text-slate-400 text-sm">No data</span>
                              )}
                            </div>
                            
                            {/* Finished Goods */}
                            <div>
                              <p className="font-semibold text-green-700 mb-2">Finished Goods (bales)</p>
                              {dc.stock_entries?.filter(e => e.product_type === 'finished_goods').map((entry, idx) => (
                                <div key={idx} className="text-sm">
                                  <span className="font-medium">{entry.pack_size_kg}kg:</span>
                                  <span className="text-slate-500 ml-1">Open:</span> {formatNumber(entry.opening_stock)} |
                                  <span className="text-slate-500 ml-1">Close:</span> {formatNumber(entry.closing_stock)}
                                </div>
                              ))}
                              {!dc.stock_entries?.some(e => e.product_type === 'finished_goods') && (
                                <span className="text-slate-400 text-sm">No data</span>
                              )}
                            </div>
                            
                            {/* Byproducts */}
                            <div>
                              <p className="font-semibold text-blue-700 mb-2">Byproducts (kg)</p>
                              {dc.stock_entries?.filter(e => e.product_type === 'byproduct').map((entry, idx) => (
                                <div key={idx} className="text-sm">
                                  <span className="font-medium">{entry.byproduct_name}:</span>
                                  <span className="text-slate-500 ml-1">Open:</span> {formatNumber(entry.opening_stock)} |
                                  <span className="text-slate-500 ml-1">Close:</span> {formatNumber(entry.closing_stock)}
                                </div>
                              ))}
                              {!dc.stock_entries?.some(e => e.product_type === 'byproduct') && (
                                <span className="text-slate-400 text-sm">No data</span>
                              )}
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingId ? 'Edit Daily Close' : 'New Daily Close'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Factory *</Label>
                <Select value={formFactory} onValueChange={setFormFactory} disabled={!!editingId}>
                  <SelectTrigger data-testid="daily-close-factory">
                    <SelectValue placeholder="Select factory" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Ndumberi">Ndumberi</SelectItem>
                    <SelectItem value="Githunguri">Githunguri</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Date</Label>
                <DatePickerField date={formDate} onDateChange={setFormDate} />
              </div>
              <div className="space-y-2">
                <Label>Time</Label>
                <Input
                  type="time"
                  value={formTime}
                  onChange={(e) => setFormTime(e.target.value)}
                  data-testid="daily-close-time"
                />
              </div>
            </div>

            {/* Raw Maize Section */}
            <div className="space-y-3 p-4 bg-amber-50 rounded-lg">
              <Label className="text-base font-semibold text-amber-800">Raw Maize (kg)</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs">Opening Stock</Label>
                  <Input
                    type="number"
                    value={rawMaizeStock.opening}
                    onChange={(e) => setRawMaizeStock({ ...rawMaizeStock, opening: e.target.value })}
                    placeholder="0"
                    data-testid="raw-maize-opening"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Closing Stock</Label>
                  <Input
                    type="number"
                    value={rawMaizeStock.closing}
                    onChange={(e) => setRawMaizeStock({ ...rawMaizeStock, closing: e.target.value })}
                    placeholder="0"
                    data-testid="raw-maize-closing"
                  />
                </div>
              </div>
            </div>

            {/* Finished Goods Section */}
            <div className="space-y-3 p-4 bg-green-50 rounded-lg">
              <Label className="text-base font-semibold text-green-800">Finished Goods (bales)</Label>
              <div className="space-y-2">
                {finishedGoodsStock.map((fg, idx) => (
                  <div key={fg.pack_size_id} className="grid grid-cols-3 gap-2 items-center">
                    <Label className="text-sm">{fg.pack_size_kg}kg Pack</Label>
                    <Input
                      type="number"
                      value={fg.opening}
                      onChange={(e) => {
                        const updated = [...finishedGoodsStock];
                        updated[idx].opening = e.target.value;
                        setFinishedGoodsStock(updated);
                      }}
                      placeholder="Opening"
                    />
                    <Input
                      type="number"
                      value={fg.closing}
                      onChange={(e) => {
                        const updated = [...finishedGoodsStock];
                        updated[idx].closing = e.target.value;
                        setFinishedGoodsStock(updated);
                      }}
                      placeholder="Closing"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Byproducts Section */}
            <div className="space-y-3 p-4 bg-blue-50 rounded-lg">
              <Label className="text-base font-semibold text-blue-800">Byproducts (kg)</Label>
              <div className="space-y-2">
                {byproductStock.map((bp, idx) => (
                  <div key={bp.name} className="grid grid-cols-3 gap-2 items-center">
                    <Label className="text-sm">{bp.name}</Label>
                    <Input
                      type="number"
                      value={bp.opening}
                      onChange={(e) => {
                        const updated = [...byproductStock];
                        updated[idx].opening = e.target.value;
                        setByproductStock(updated);
                      }}
                      placeholder="Opening"
                    />
                    <Input
                      type="number"
                      value={bp.closing}
                      onChange={(e) => {
                        const updated = [...byproductStock];
                        updated[idx].closing = e.target.value;
                        setByproductStock(updated);
                      }}
                      placeholder="Closing"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea
                value={formNotes}
                onChange={(e) => setFormNotes(e.target.value)}
                placeholder="Any notes for this daily close..."
                rows={2}
              />
            </div>

            <Button type="submit" className="w-full btn-primary" data-testid="save-daily-close-btn">
              {editingId ? 'Update Daily Close' : 'Save Daily Close'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="sm:max-w-xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Edit History - {selectedRecord?.date} ({selectedRecord?.factory})
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {editHistory.length === 0 ? (
              <p className="text-slate-500 text-center py-4">No edit history</p>
            ) : (
              editHistory.map((edit, idx) => (
                <Card key={edit.id} className="bg-slate-50">
                  <CardContent className="pt-4">
                    <div className="flex justify-between items-start mb-2">
                      <Badge variant="outline">Edit #{editHistory.length - idx}</Badge>
                      <span className="text-xs text-slate-500">{edit.edited_at}</span>
                    </div>
                    <p className="text-xs text-slate-500 mb-2">Edited by: {edit.edited_by}</p>
                    <div className="text-sm">
                      <p className="font-medium mb-1">Previous Values:</p>
                      {edit.previous_values?.stock_entries?.map((entry, i) => (
                        <div key={i} className="text-xs text-slate-600">
                          {entry.product_type === 'raw_maize' && `Raw Maize: ${entry.opening_stock} → ${entry.closing_stock}`}
                          {entry.product_type === 'finished_goods' && `${entry.pack_size_kg}kg: ${entry.opening_stock} → ${entry.closing_stock}`}
                          {entry.product_type === 'byproduct' && `${entry.byproduct_name}: ${entry.opening_stock} → ${entry.closing_stock}`}
                        </div>
                      ))}
                      {edit.previous_values?.notes && (
                        <p className="text-xs text-slate-600">Notes: {edit.previous_values.notes}</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
